import { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Store } from 'types/store';
import { Vehicle, VehicleAddAmendment, VehicleAmendment, VehicleModifyAmendment } from 'types/expiryRenewal';
import { setAmendments } from 'actions/expiryRenewalActions';
import useVehicleForm from './useVehicleForm';
import { buildTakenRegs, buildVehicleAmendFields, isVehicleAdd, isVehicleModify } from '../utils/amendmentUtils';

export type { FieldErrors } from './useVehicleForm';

const useEditVehicle = (vehicle: Vehicle, onClose: () => void) => {
  const dispatch = useDispatch();
  const pageModel = useSelector((state: Store) => state.expiryRenewal.pageModel);
  const existingAmendments = useSelector((state: Store) => state.expiryRenewal.amendments);

  const priorAmendment = existingAmendments
    .find((a): a is VehicleAmendment => (isVehicleModify(a) && a.vehicleAmend.oldRegistration === vehicle.Registration)
      || (isVehicleAdd(a) && a.vehicleAdd.registration === vehicle.Registration));

  const priorRegistration = priorAmendment?.amendmentType === 'Add'
    ? priorAmendment.vehicleAdd.registration
    : priorAmendment?.vehicleAmend.newRegistration;

  const priorVehicleFields = priorAmendment?.amendmentType === 'Add'
    ? priorAmendment.vehicleAdd
    : priorAmendment?.vehicleAmend;

  const initialFields: Vehicle = {
    Registration: priorRegistration ?? vehicle.Registration,
    Make: priorVehicleFields?.make ?? vehicle.Make,
    Model: priorVehicleFields?.model ?? vehicle.Model,
    YearOfManufacture: priorVehicleFields ? String(priorVehicleFields.yearOfManufacture) : vehicle.YearOfManufacture,
  };

  const initialFieldsRef = useRef(initialFields);

  const takenRegs = useMemo(
    () => buildTakenRegs(pageModel?.vehicles ?? [], existingAmendments, vehicle.Registration),
    [pageModel?.vehicles, existingAmendments, vehicle.Registration],
  );

  const computeIsDuplicate = useCallback((reg: string) => takenRegs.has(reg), [takenRegs]);

  const {
    fields,
    searchedFuelType,
    touched,
    loading,
    errors,
    searchAndRegError,
    isVehicleNotFound,
    clearRegistrationSearchError,
    setTouched,
    handleChange,
    handleBlur,
    handleSearch,
  } = useVehicleForm(initialFields, computeIsDuplicate);

  const hasErrors = Object.values(errors).some(Boolean);
  const anyTouched = Object.values(touched).some(Boolean);
  const isSaveDisabled = loading || (hasErrors && anyTouched);

  const handleSave = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ Registration: true, Make: true, Model: true, YearOfManufacture: true });
    if (hasErrors || !pageModel) return;

    const initial = initialFieldsRef.current;

    const isSame = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

    const hasChanges = !isSame(fields.Registration, initial.Registration)
      || !isSame(fields.Make, initial.Make)
      || !isSame(fields.Model, initial.Model)
      || !isSame(fields.YearOfManufacture, initial.YearOfManufacture);

    if (hasChanges) {
      const matchedAmendment = existingAmendments.find(
        (a) => (isVehicleModify(a) && a.vehicleAmend.oldRegistration === vehicle.Registration)
          || (isVehicleAdd(a) && a.vehicleAdd.registration === vehicle.Registration),
      );

      const otherAmendments = existingAmendments.filter((a) => a !== matchedAmendment);

      const vehicleBase = buildVehicleAmendFields(fields, searchedFuelType);

      const newAmendment: VehicleAmendment = matchedAmendment?.amendmentType === 'Add'
        ? ({
          productCode: 'ExtraVehicle',
          amendmentType: 'Add',
          vehicleAdd: { ...vehicleBase, registration: fields.Registration },
        } as VehicleAddAmendment)
        : ({
          productCode: 'Vehicle',
          amendmentType: 'Modify',
          vehicleAmend: {
            ...vehicleBase,
            oldRegistration: (matchedAmendment as VehicleModifyAmendment | undefined)?.vehicleAmend.oldRegistration ?? vehicle.Registration,
            newRegistration: fields.Registration,
          },
        } as VehicleModifyAmendment);
      dispatch(setAmendments([...otherAmendments, newAmendment]));
    }

    onClose();
  }, [fields, hasErrors, pageModel, vehicle, existingAmendments, searchedFuelType, dispatch, onClose, setTouched]);

  return {
    fields,
    errors,
    touched,
    loading,
    isSaveDisabled,
    searchAndRegError,
    isVehicleNotFound,
    clearRegistrationSearchError,
    handleChange,
    handleBlur,
    handleSearch,
    handleSave,
  };
};

export default useEditVehicle;
